import AppImage from '../AppImage'
import Link from 'next/link'

import { trim } from '@/lib/sanitise-strings'

type BlockquoteProps = {
  quote: string
  extraText?: string // smaller text under the quote
  personLink?: string
  personName?: string
  personTitle?: string
  personImage?: {
    url: string
    alt: string
  }
}

// TODO: Migration script to wrap all of these? Change to always have background?

export default function Blockquote({
  quote,
  extraText,
  personName,
  personTitle,
  personImage,
  personLink,
}) {
  const { url: imageUrl, alt: imageAlt } = personImage ?? {}

  const personWrapClass = `
    grid
    ${personImage ? 'grid-cols-[80px,1fr] ms:grid-cols-[60px,1fr] @is-child-of/nested-flex:!grid-cols-[60px,1fr] xs:!grid-cols-1' : 'grid-cols-1'}

    items-center 
    gap-m

    xs:gap-s
  `

  const imageClass = `
    aspect-square 
    w-full
    overflow-hidden 
    rounded-full 
    xs:w-[60px]
  `

  const personImageEl = personImage ? (
    <AppImage
      path={imageUrl}
      alt={imageAlt}
      height={200}
      width={200}
      transforms={{
        height: '200',
        width: '200',
      }}
      sizes="min(200px, calc(100vw - 40px))"
      srcSet={{
        200: { width: '200', height: '200', dpr: '1' },
        300: { width: '300', height: '300', dpr: '1' },
        400: { width: '400', height: '400', dpr: '1' },
        500: { width: '500', height: '500', dpr: '1' },
        600: { width: '600', height: '600', dpr: '1' },
      }}
      className={!personLink ? imageClass : 'pointer-events-none'}
    />
  ) : null

  // Remove quotes from the beginning and end of the quote (Louise request)
  const sanitisedQuote = quote ? trim(quote, '"') : null

  const wrapClass = `
    flex 
    items-start 
    justify-between 

    s-up:gap-l

    s:justify-start
    s:gap-y-m 
    s:gap-x-xs 
    s:flex-wrap

    @is-child-of/nested-flex:rounded-md
    @is-child-of/nested-flex:!justify-start
    @is-child-of/nested-flex:!gap-y-m 
    @is-child-of/nested-flex:!gap-x-xs 
    @is-child-of/nested-flex:!flex-wrap
    @is-child-of/nested-flex:p-l
    
    @is-child-of/nested-flex:theme--base-0:theme--gradient-light
    @is-child-of/nested-flex:theme--base-0:gradient-angle-[to_right]

    @is-child-of/nested-flex:theme--primary-50:theme--base-0
    @is-child-of/nested-flex:theme--primary-50:bg-theme
    
    @is-child-of/nested-flex:theme--gradient-light:theme--base-0
    @is-child-of/nested-flex:theme--gradient-light:bg-theme

  `

  const leftQuoteClass = `
    aspect-[52/40] 
    w-[52px] 
    text-fg-title 
    
    ms:w-[40px] 
    
    s:w-[32px]
    s:order-1 

    @is-child-of/nested-flex:w-[32px] 
    @is-child-of/nested-flex:order-1 
  `

  const rightQuoteClass = `
    aspect-[52/40] 
    w-[52px] 
    text-fg-title 
    
    ms:w-[40px] 
    
    s:hidden

    @is-child-of/nested-flex:hidden 
  `

  const quoteStyles = `
    w-full 
    
    @m-up:max-w-[var(--container-s-max-w)] 

    s:order-3

    @is-child-of/nested-flex:order-3 
  `

  const headingClass = `
    h2 
    mb-0
    
    @is-child-of/nested-flex:text-[length:--h3-size] 
  `

  return (
    <div className="component component--blockquote">
      <div className="container relative @container @[0px]/single-simple:!max-w-[var(--container-s-max-w)]">
        <div className={wrapClass}>
          <svg aria-hidden="true" className={leftQuoteClass}>
            <use href={`${process.env.NEXT_PUBLIC_SPRITE_PATH}#icon-quote-left`}></use>
          </svg>

          <div className={quoteStyles}>
            <blockquote className="c-blockquote flex flex-col gap-xxl s:gap-l">
              <div className="flex flex-col gap-m">
                <span
                  className={headingClass}
                  dangerouslySetInnerHTML={{ __html: sanitisedQuote }}
                />

                {extraText ? <div dangerouslySetInnerHTML={{ __html: extraText }}></div> : null}
              </div>

              {personName ? (
                <div className={personWrapClass}>
                  {personLink ? (
                    <Link className={imageClass} href={personLink}>
                      {personImageEl}
                    </Link>
                  ) : (
                    personImageEl
                  )}

                  <div className="flex flex-col gap-xxxs">
                    <span className="h5 mb-0">{personName}</span>
                    {personTitle ? <span className="p--xsmall">{personTitle}</span> : null}
                  </div>
                </div>
              ) : null}
            </blockquote>
          </div>

          <svg aria-hidden="true" className={rightQuoteClass}>
            <use href={`${process.env.NEXT_PUBLIC_SPRITE_PATH}#icon-quote-right`}></use>
          </svg>
        </div>
      </div>
    </div>
  )
}
